import { type Company, type CompanyFeatureSettings } from "@finway-group/shared/lib/models"
import { type CompanyAccoutingSettings } from "@finway-group/shared/lib/models/company/company.interface"
import { type Dispatch } from "redux"
import { AuthnService, CompanyService } from "~Shared/services"
import {
    CompanyActionTypes,
    type CreateCompanyAction,
    type FetchAllCompaniesAction,
    type MarkCompanyAsDeletedAction,
    type SelectCompanyAction,
    type UpdateCompanyAction,
} from "~Shared/store/actions/company/companyTypes"
import { resetCardOverviewData } from "../analytics/analyticsActions"
import { createLoginAction, setIsWeavrAuthenticated } from "../auth/authActions"
import { resetWallet } from "../corporate/corporateActions"
import { resetCostCenter } from "../costCenter/costCenterActions"
import { setLoading } from "../fetcher/fetcherActions"
import { FetcherActionTypes } from "../fetcher/fetcherTypes"
import { resetImportResults } from "../import/importActions"
import { resetLiquidities } from "../liquidity/liquidityActions"
import { resetAllTables } from "../tables/tableActions"

export const updateCompany = (id: string, updatedCompany: Partial<Company>) => async (dispatch: Dispatch) => {
    const company = await CompanyService.updateCompany(id, updatedCompany)
    dispatchUpdateCompanyAction(company, dispatch)
}

export const updateCompanyFeatureSettings = (id: string, updatedCompanyFeatureSettings: Partial<CompanyFeatureSettings>) => async (dispatch: Dispatch) => {
    const company = await CompanyService.updateFeatureSettings(id, updatedCompanyFeatureSettings)
    dispatchUpdateCompanyAction(company, dispatch)
}

export const updateCompanyAccountingSettings = (id: string, updateAccountingSettings: CompanyAccoutingSettings) => async (dispatch: Dispatch) => {
    const company = await CompanyService.updateAccountingSettings(id, updateAccountingSettings)
    dispatchUpdateCompanyAction(company, dispatch)
}

export const dispatchUpdateCompanyAction = (company: Company, dispatch: Dispatch) => {
    const updateCompanyAction: UpdateCompanyAction = {
        type: CompanyActionTypes.UPDATE_COMPANY,
        company,
    }

    dispatch(updateCompanyAction)
}

export const fetchAllCompanies =
    (shouldRefetch: boolean = true) =>
    async (dispatch: Dispatch) => {
        // temporary until we update shared to include id instead of _id
        const companies = await CompanyService.fetchCompanies()

        const { companyId } = AuthnService.getDataFromAccessToken()

        const selectedCompany = companies.find(({ _id }) => _id === companyId)

        const fetchAllCompaniesAction: FetchAllCompaniesAction = {
            type: CompanyActionTypes.FETCH_ALL_COMPANIES,
            companies,
            company: { ...selectedCompany, shouldRefetch },
        }

        dispatch(fetchAllCompaniesAction)
        return companies
    }

export const createCompany = (newCompany: Company) => async (dispatch: Dispatch) => {
    const company = await CompanyService.createCompany(newCompany)

    const createCompanyAction: CreateCompanyAction = {
        type: CompanyActionTypes.CREATE_COMPANY,
        company,
    }

    dispatch(createCompanyAction)

    return company
}

export const selectCompany = (company: Company) => async (dispatch: Dispatch) => {
    try {
        setLoading()(dispatch)

        const selectCompanyAction: SelectCompanyAction = {
            type: CompanyActionTypes.SELECT_COMPANY,
            company,
        }
        const { accessToken } = await AuthnService.setCurrentCompany(company._id)
        const loginAction = createLoginAction(accessToken)

        dispatch(loginAction)
        dispatch(selectCompanyAction)

        dispatch(resetAllTables())
        dispatch(resetImportResults())
        // reset cost center item
        dispatch(resetCostCenter())
        resetLiquidities()(dispatch)
        resetWalletInformation()(dispatch)
        setIsWeavrAuthenticated(false)(dispatch)
    } catch (err) {
        dispatch({
            type: FetcherActionTypes.LOADED,
            hasLoaded: true,
        })
        throw err
    }
}

export const resetWalletInformation = () => (dispatch: Dispatch) => {
    resetWallet()(dispatch)
    resetCardOverviewData()(dispatch)
}

export const markCompanyAsDeleted = (id: string) => async (dispatch: Dispatch) => {
    const deletedCompany = await CompanyService.deleteCompany(id)
    const markCompanyAsDeletedAction: MarkCompanyAsDeletedAction = {
        type: CompanyActionTypes.MARK_AS_DELETED,
        company: deletedCompany,
    }

    dispatch(markCompanyAsDeletedAction)
    return deletedCompany
}

export const setCompany = (company: Company) => (dispatch: Dispatch) => {
    const updateCompanyAction: UpdateCompanyAction = {
        type: CompanyActionTypes.UPDATE_COMPANY,
        company,
    }

    dispatch(updateCompanyAction)
    return company
}
